/**
 * Sorting any table — one comparator, used by every sortable heading.
 *
 * Lives here rather than in the component so the rules below are tested without a browser
 * and cannot be re-decided per table.
 */
/**
 * ⚠ **A missing value sorts LAST in both directions, always.**
 *
 * Monday boards are full of them — on Quality Audits the POP is often blank and most
 * dispatched observations carry no contractor status. They are real rows and must stay
 * findable, but "not filled in" is not a value: floating the board's data-entry gaps above
 * the work on every descending sort would make the top of every table useless.
 *
 * Numbers compare numerically, strings with `localeCompare`, and a mixed column compares as
 * strings so it cannot throw.
 */
export function compareSortable(a, b, direction = "asc") {
    if (a === null && b === null)
        return 0;
    if (a === null)
        return 1;
    if (b === null)
        return -1;
    const sign = direction === "desc" ? -1 : 1;
    if (typeof a === "number" && typeof b === "number")
        return sign * (a - b);
    return sign * String(a).localeCompare(String(b));
}
/**
 * Sort rows by one accessor.
 *
 * ⚠ **Stable**, so rows the comparator calls equal keep the order they arrived in. That is
 * what makes a sort on a column of mostly-equal values — a status column where most rows
 * read `Dispatched`, say — look like the same table reordered rather than reshuffled at
 * random.
 */
export function sortRowsBy(rows, value, direction = "asc") {
    return [...rows].sort((a, b) => compareSortable(value(a), value(b), direction));
}
/**
 * Which way a column sorts when you first click it.
 *
 * ⚠ A count opens biggest-first and a name A-to-Z, because that is what the click means:
 * "which partner has the most open P1 punches" is the question, and answering it with the
 * zeroes at the top would take a second click every time.
 */
export function defaultDirectionForValue(sample) {
    return typeof sample === "number" ? "desc" : "asc";
}
//# sourceMappingURL=sort.js.map